import * as THREE from 'three';
import { createText, extrudeShape, makeExtrusionMaterial, makeShape, parseSVG } from 'src/pages/plan/utils/objects';
import planConfig from 'src/config/plan';
import Polygon from 'src/utils/shape_processor/classes/polygon';

function extrudeSVG(svg, config, shapeCallback: any = null) {
	const paths = parseSVG(svg);

	const group = new THREE.Group();

	paths.forEach((path) => {
		path.shapes.forEach((shape) => {
			const geometry = extrudeShape(shape, config);

			const material = makeExtrusionMaterial(config);

			const mesh = new THREE.Mesh(geometry, material);
			mesh.castShadow = true;
			mesh.receiveShadow = true;

			if (shapeCallback instanceof Function) {
				shapeCallback(path, mesh, geometry);
			}

			group.add(mesh);
		});
	});

	if (!config.noTranslate) {
		group.translateY(config.depth);
	}

	return group;
}

export function createWalls(borderSVG) {
	const wallsConfig = planConfig.objects.walls;

	return extrudeSVG(borderSVG, wallsConfig);
}

export function createFloor(floorSVG) {
	const floorConfig = planConfig.objects.floor;

	return extrudeSVG(floorSVG, floorConfig);
}

export function createPublic(publicSVG, placeCallback = null) {
	const placesConfig = planConfig.objects.places;

	return extrudeSVG(publicSVG, placesConfig, placeCallback);
}

export function createBooth(booth) {
	const boothsConfig = planConfig.objects.booths;

	const [shape, pos]: any[] = makeShape(booth.bounds);
	const geometry = extrudeShape(shape, boothsConfig);

	const material = makeExtrusionMaterial(boothsConfig);

	const mesh = new THREE.Mesh(geometry, material);
	mesh.castShadow = true;
	mesh.receiveShadow = true;

	mesh.position.set(pos.x, boothsConfig.depth, pos.y);

	return mesh;
}

export function createBooths(booths, boothCallback: any = null) {
	const boothsGroup = new THREE.Group();

	booths.forEach((booth) => {
		const boothMesh = createBooth(booth);

		if (boothCallback instanceof Function) {
			boothCallback(booth, boothMesh);
		}

		boothsGroup.add(boothMesh);
	});

	return boothsGroup;
}

export function detectConnectedCompaniesToPath(path, companyBoothMap) {
	const connectedCompanies = Object.values(companyBoothMap)
		.map((companyBooth: any) => {
			let pathIndex = -1;
			let isSourceOrTarget = false;

			pathIndex = path.findIndex((n) => n.id === companyBooth.node?.id);
			if (pathIndex === -1) {
				pathIndex = path.findIndex((n) => !!n.links.find((ln) => ln.id === companyBooth.node?.id));
			} else {
				isSourceOrTarget = true;
			}

			const isConnected = pathIndex >= 0;

			return { ...companyBooth, isConnected, isSourceOrTarget, pathIndex };
		})
		.filter((companyBooth) => companyBooth.isConnected && !!companyBooth.company);

	connectedCompanies.sort((a, b) => a.pathIndex - b.pathIndex);

	return connectedCompanies;
}

export function createBoothLabelsForPath(path, connectedCompanies, font, size = 8, depth = 0.2, color = 0xffffff) {
	const labelsGroup = new THREE.Group();

	const last = connectedCompanies.pop();
	connectedCompanies.unshift(last);

	const printedTextBoxes: any[] = [];
	connectedCompanies.forEach((companyBooth) => {
		const center = companyBooth.polygon.getVisualCenter();

		const txt = createText(
			companyBooth.company.legal_name_fa,
			font,
			size,
			depth,
			{ x: center.x, y: planConfig.objects.booths.depth + 2, z: center.y },
			color
		);
		const textBox = Polygon.fromRect({
			x: center.x - txt.userData.dimensions.width / 2,
			y: center.y - txt.userData.dimensions.height / 2,
			width: txt.userData.dimensions.width,
			height: txt.userData.dimensions.height,
		}).boundingBox;

		const collision = printedTextBoxes.find((box) => box.overlapsWith(textBox));
		if (!collision) {
			printedTextBoxes.push(textBox);
			labelsGroup.add(txt);
		}
	});

	return labelsGroup;
}
